/**
 * Member portal design kit — the single source of visual primitives for every
 * /member page. Uses only theme tokens (primary-* is tenant-branded at runtime).
 */
import { forwardRef, type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils/cn";

import { EASE, fadeUp, surfaceClass } from "./styles";

type IconType = LucideIcon;

// ── Motion ──────────────────────────────────────────────────────────────────

/** Page wrapper: staggers direct <Reveal> children in on mount. */
export function MemberPage({ children, className }: { children: ReactNode; className?: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : "hidden"}
      animate="show"
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
      className={cn("space-y-5 md:space-y-6", className)}
    >
      {children}
    </motion.div>
  );
}

/** One staggered block inside <MemberPage>. */
export function Reveal({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div variants={fadeUp} className={className}>
      {children}
    </motion.div>
  );
}

// ── Page header ─────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Reveal className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary-600 dark:text-primary-400">
            {eyebrow}
          </p>
        )}
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-ink-900 md:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1 text-sm leading-relaxed text-ink-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </Reveal>
  );
}

// ── Surfaces ────────────────────────────────────────────────────────────────

export const Surface = forwardRef<
  HTMLDivElement,
  { children: ReactNode; className?: string; padded?: boolean; interactive?: boolean } & React.HTMLAttributes<HTMLDivElement>
>(({ children, className, padded = true, interactive = false, ...rest }, ref) => (
  <div
    ref={ref}
    className={cn(
      surfaceClass,
      padded && "p-5",
      interactive &&
        "transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(15,46,38,0.08)]",
      className,
    )}
    {...rest}
  >
    {children}
  </div>
));
Surface.displayName = "Surface";

/** Card with a titled header row and optional trailing action. */
export function SectionCard({
  title,
  subtitle,
  icon: Icon,
  action,
  children,
  className,
  bodyClassName,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: IconType;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn(surfaceClass, "overflow-hidden", className)}>
      <header className="flex items-center gap-3 px-5 pt-5">
        {Icon && <IconBadge icon={Icon} />}
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold text-ink-900">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-ink-500">{subtitle}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      <div className={cn("p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

// ── Small building blocks ───────────────────────────────────────────────────

export type Tone = "primary" | "success" | "warning" | "danger" | "info" | "neutral";

const toneSoft: Record<Tone, string> = {
  primary: "bg-primary-500/10 text-primary-700 dark:text-primary-300",
  success: "bg-success-500/10 text-success-500",
  warning: "bg-warning-500/[0.12] text-warning-500",
  danger: "bg-danger-500/10 text-danger-500",
  info: "bg-info-500/10 text-info-500",
  neutral: "bg-ink-900/[0.05] text-ink-700 dark:bg-white/[0.06]",
};

export function IconBadge({ icon: Icon, tone = "primary", size = "md" }: { icon: IconType; tone?: Tone; size?: "sm" | "md" | "lg" }) {
  const box = size === "sm" ? "h-8 w-8 rounded-xl" : size === "lg" ? "h-12 w-12 rounded-2xl" : "h-10 w-10 rounded-[14px]";
  const ic = size === "sm" ? "h-4 w-4" : size === "lg" ? "h-6 w-6" : "h-5 w-5";
  return (
    <span className={cn("inline-flex shrink-0 items-center justify-center", box, toneSoft[tone])}>
      <Icon className={ic} aria-hidden="true" />
    </span>
  );
}

export function StatusPill({ tone = "neutral", children, dot = true }: { tone?: Tone; children: ReactNode; dot?: boolean }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold", toneSoft[tone])}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />}
      {children}
    </span>
  );
}

export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = "primary",
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  icon?: IconType;
  tone?: Tone;
}) {
  return (
    <div className="rounded-2xl border border-border-100 bg-bg/60 p-4 dark:bg-white/[0.03]">
      <div className="flex items-center gap-2">
        {icon && <IconBadge icon={icon} tone={tone} size="sm" />}
        <p className="text-xs font-medium text-ink-500">{label}</p>
      </div>
      <p className="mt-2 text-xl font-semibold tabular-nums tracking-tight text-ink-900">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-ink-400">{hint}</p>}
    </div>
  );
}

/** Label/value pair for detail grids. */
export function Field({ label, value, mono }: { label: ReactNode; value: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-ink-500">{label}</p>
      <p className={cn("mt-1 truncate text-sm font-semibold text-ink-900", mono && "tabular-nums tracking-wide")}>{value}</p>
    </div>
  );
}

export function Progress({ value, tone = "primary", className }: { value: number; tone?: Tone; className?: string }) {
  const bar: Record<Tone, string> = {
    primary: "bg-primary-500",
    success: "bg-success-500",
    warning: "bg-warning-500",
    danger: "bg-danger-500",
    info: "bg-info-500",
    neutral: "bg-ink-400",
  };
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className={cn("h-2 w-full overflow-hidden rounded-full bg-ink-900/[0.06] dark:bg-white/[0.08]", className)}>
      <motion.div
        className={cn("h-full rounded-full", bar[tone])}
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: EASE }}
      />
    </div>
  );
}

/** Tappable list row — icon, title, subtitle, trailing slot, chevron when linked. */
export function ListRow({
  icon,
  tone,
  title,
  subtitle,
  trailing,
  to,
  onClick,
}: {
  icon?: IconType;
  tone?: Tone;
  title: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  to?: string;
  onClick?: () => void;
}) {
  const body = (
    <>
      {icon && <IconBadge icon={icon} tone={tone} size="sm" />}
      <div className="min-w-0 flex-1 text-start">
        <p className="truncate text-sm font-semibold text-ink-900">{title}</p>
        {subtitle && <p className="mt-0.5 truncate text-xs text-ink-500">{subtitle}</p>}
      </div>
      {trailing}
      {(to || onClick) && <ChevronRight className="h-4 w-4 shrink-0 text-ink-400 rtl:rotate-180" aria-hidden="true" />}
    </>
  );
  const cls =
    "flex w-full items-center gap-3 rounded-2xl px-3 py-3 transition-colors hover:bg-ink-900/[0.03] dark:hover:bg-white/[0.04] " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500";
  if (to) return <Link to={to} className={cls}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className={cls}>{body}</button>;
  return <div className={cls.replace(/hover:\S+/g, "")}>{body}</div>;
}

/** Empty / zero state with optional illustration and action. */
export function EmptyState({
  icon: Icon,
  image,
  title,
  description,
  action,
  compact,
}: {
  icon?: IconType;
  image?: string;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cn("flex flex-col items-center text-center", compact ? "py-6" : "py-10")}>
      {image ? (
        <img src={image} alt="" aria-hidden="true" draggable={false} className="mb-4 h-28 w-auto select-none object-contain opacity-40 grayscale-[30%] [mask-image:linear-gradient(to_bottom,black_55%,transparent)] dark:opacity-30" />
      ) : Icon ? (
        <span className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500/10 text-primary-600 dark:text-primary-400">
          <Icon className="h-7 w-7" aria-hidden="true" />
        </span>
      ) : null}
      <p className="text-[15px] font-semibold text-ink-900">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm leading-relaxed text-ink-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Shimmer placeholder block. */
export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-xl bg-ink-900/[0.06] dark:bg-white/[0.06]", className)} />;
}

/** Inline alert / notice. */
export function Notice({ tone = "info", icon: Icon, children }: { tone?: Tone; icon?: IconType; children: ReactNode }) {
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={cn("flex items-start gap-3 rounded-2xl px-4 py-3 text-sm", toneSoft[tone])}>
      {Icon && <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}
      <div className="min-w-0 text-ink-700 dark:text-ink-900">{children}</div>
    </div>
  );
}

// ── Buttons ─────────────────────────────────────────────────────────────────

/** Large tile used for quick actions on dashboards. */
export function ActionTile({ to, icon: Icon, label, hint, tone = "primary" }: { to: string; icon: IconType; label: ReactNode; hint?: ReactNode; tone?: Tone }) {
  return (
    <Link
      to={to}
      className={cn(
        surfaceClass,
        "group flex flex-col gap-3 p-4 transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_28px_rgba(15,46,38,0.08)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
      )}
    >
      <span className={cn("inline-flex h-11 w-11 items-center justify-center rounded-[14px] transition-transform duration-200 group-hover:scale-105", toneSoft[tone])}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span>
        <span className="block text-sm font-semibold text-ink-900">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-ink-500">{hint}</span>}
      </span>
    </Link>
  );
}

